import { Link, useParams } from "@tanstack/react-router";
import type { ReactNode } from "react";

import page from "../actors/actors.module.css";
import { SoonControl } from "../Soon";
import classes from "./simple-sections.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/studio/simple/brand";

/** The prototype's Brand tabs, in its order: `?tab=`, none for Brand kit, the page's first. */
export const BRAND_TABS = ["kit", "products", "actors", "voices", "integrations"] as const;
export type BrandTab = (typeof BRAND_TABS)[number];

const LABELS: Record<BrandTab, string> = {
  kit: "Brand kit",
  products: "Products",
  actors: "Actors",
  voices: "Voices",
  integrations: "Integrations",
};

export interface SimpleBrandProps {
  tab: BrandTab;
  /**
   * Each tab built so far, composed where the brands area's own pages may be drawn (`app/`); a
   * tab with none is Coming soon, and its link opens Brand kit.
   */
  panels: { kit: ReactNode } & Partial<Record<BrandTab, ReactNode>>;
}

/**
 * Simple's Brand (spec ui-perf/simple-brand): the Created AI Simple prototype's `VIEWS.brand`, its
 * tabs over the brand's kit, products, actors, voices and integrations, in Pro's tabs' look.
 */
export function SimpleBrand({ tab, panels }: SimpleBrandProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const open = panels[tab] === undefined ? "kit" : tab;
  return (
    <div className={classes.page}>
      <h1 className={classes.title}>Brand</h1>
      <nav className={`${page.tabs} ${classes.pageTabs}`} aria-label="Brand tabs">
        {BRAND_TABS.map((each) =>
          panels[each] === undefined ? (
            <SoonControl key={each} className={page.tab}>
              {LABELS[each]}
            </SoonControl>
          ) : (
            <Link
              key={each}
              to="/teams/$teamId/brands/$brandId/simple/brand"
              params={{ teamId, brandId }}
              search={{ tab: each === "kit" ? undefined : each }}
              className={page.tab}
              aria-current={each === open ? "page" : undefined}
              activeOptions={{ exact: true, includeSearch: true }}
            >
              {LABELS[each]}
            </Link>
          ),
        )}
      </nav>
      {/* Another brand starts each tab anew (the brand menu keeps the page mounted). */}
      <div key={`${teamId} ${brandId} ${open}`}>{panels[open]}</div>
    </div>
  );
}
